/**
 * Web sibling of idleLockPreferences.
 *
 * Same storage key, different range. The phone locks after 2 minutes by
 * default because Face ID makes unlocking free. On the web the unlock is a
 * typed password (or a fresh sign-in for an account with an authenticator),
 * and since the web also locks a visible tab nobody is touching
 * (web/src/shell/inactivityLock.ts), a short timer locks people out while
 * they read. Owner decision 2026-09-18: the web offers 15 minutes and up,
 * 15 by default. A shorter value left in storage by an earlier build is
 * read as 15 minutes.
 * Metro never picks `.web.*` files.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';

// Deliberately no import of the phone module: a `.web.ts` file cannot name
// its own sibling without resolving back to itself, and these few lines are
// all it would take from it. The key must stay equal to the phone's.
const STORAGE_KEY = '@idle_lock_threshold_ms';

export interface IdleLockOption {
  readonly ms: number;
  readonly label: string;
}

const MINUTE = 60 * 1000;
const MIN_THRESHOLD_MS = 15 * MINUTE;
const MAX_THRESHOLD_MS = 60 * MINUTE;
const WEB_DEFAULT_THRESHOLD_MS = MIN_THRESHOLD_MS;

export const IDLE_LOCK_OPTIONS: readonly IdleLockOption[] = [
  { ms: 15 * MINUTE, label: '15 minutes' },
  { ms: 30 * MINUTE, label: '30 minutes' },
  { ms: 60 * MINUTE, label: '1 hour' },
] as const;

export const DEFAULT_IDLE_LOCK_THRESHOLD_MS = WEB_DEFAULT_THRESHOLD_MS;

export async function getIdleLockThreshold(): Promise<number> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return WEB_DEFAULT_THRESHOLD_MS;
    const parsed = parseInt(raw, 10);
    if (!Number.isFinite(parsed) || parsed < MIN_THRESHOLD_MS || parsed > MAX_THRESHOLD_MS) {
      return WEB_DEFAULT_THRESHOLD_MS;
    }
    return parsed;
  } catch {
    return WEB_DEFAULT_THRESHOLD_MS;
  }
}

export async function setIdleLockThreshold(ms: number): Promise<void> {
  if (!Number.isFinite(ms) || ms < MIN_THRESHOLD_MS || ms > MAX_THRESHOLD_MS) {
    throw new Error(`idle-lock threshold out of range [${MIN_THRESHOLD_MS}, ${MAX_THRESHOLD_MS}]: ${ms}`);
  }
  await AsyncStorage.setItem(STORAGE_KEY, String(ms));
}

export const __internal = {
  STORAGE_KEY,
  MIN_THRESHOLD_MS,
  MAX_THRESHOLD_MS,
};
